import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import he from 'he';
import { ProductAmount, ProductData } from 'types/ProductData';
import { Button, CustomGrid, DescriptionWithShowMore, Tabs } from 'ui';
import { RenderProductDelivery } from 'utils/productCardHelpers';
import { useBreakpoint } from 'utils/useBreakpoint';
import { StyledPriceAvailabilityCard } from '../style';

export enum ProductTabKey {
  PRICE_AVAILABILITY = 'price_availability',
  PRODUCER = 'producer',
  INGREDIENTS = 'ingredients',
  DELIVERY = 'delivery',
  REVIEWS = 'reviews',
}

const GRAM_NUTRIENTS = [
  'fat',
  'saturated_fat',
  'carbohydrates',
  'sugars',
  'proteins',
  'salt',
];

interface ProductTabsProps {
  product?: ProductData;
  productAmount?: ProductAmount;
  isEmarket: boolean;
  onGoToFarm: () => void;
}

const ProductTabs = ({
  product,
  productAmount,
  isEmarket,
  onGoToFarm,
}: ProductTabsProps) => {
  const intl = useIntl();
  const { isMdDown } = useBreakpoint();

  const hasNutritionalValues = product?.nutritional_values
    ? Object.values(product.nutritional_values).some((value) => value > 0)
    : false;
  const hasIngredients = !!product?.ingredients || hasNutritionalValues;
  const hasDeliveries = !!product?.farm_deliveries?.length && isEmarket;
  const hasReviews = false; // TODO new functionality
  const hasPriceAvailability = !isEmarket && !!productAmount;

  const farmDescription = useMemo(
    () =>
      product?.farm_description
        ? he
            .decode(product.farm_description as string)
            .replace(/<\/?[^>]+(>|$)/g, '')
        : '',
    [product?.farm_description]
  );

  const renderPriceAvailability = () => {
    if (!productAmount) return null;

    const priceRanges = productAmount.wholesale_price_ranges ?? [];
    const reserves = productAmount.wholesale_reserves ?? [];
    const minRangeFrom = priceRanges.length
      ? Math.min(...priceRanges.map((value) => value.amount_from))
      : 0;

    return (
      <CustomGrid inOneRow={2} gapRow={20}>
        <StyledPriceAvailabilityCard>
          <div className={'price-availability-title'}>
            {intl.formatMessage({ id: 'products.price' })}:
          </div>
          <div className={'price-availability-content'}>
            {priceRanges.length ? (
              <>
                {minRangeFrom >= 1 && (
                  <div>
                    {intl.formatMessage(
                      { id: 'product.price_up_to' },
                      {
                        amount_to: minRangeFrom,
                        price: Number(
                          productAmount.storage_stock_price
                        ).toFixed(2),
                        type: productAmount.storage_unit,
                      }
                    )}
                  </div>
                )}
                {priceRanges.map((value) => (
                  <div key={`${value.amount_from}-${value.amount_to}`}>
                    {intl.formatMessage(
                      { id: 'product.price_ranges' },
                      {
                        amount_from: value.amount_from,
                        amount_to: value.amount_to,
                        price: value.price,
                        type: productAmount.storage_unit,
                      }
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div>
                {Number(productAmount.storage_stock_price).toFixed(2)} €/
                {productAmount.storage_unit}
              </div>
            )}
          </div>
        </StyledPriceAvailabilityCard>
        <StyledPriceAvailabilityCard>
          <div className={'price-availability-title'}>
            {intl.formatMessage({ id: 'products.availability' })}:
          </div>
          <div className={'price-availability-content'}>
            <div>
              {intl.formatMessage(
                { id: 'product.availability_amount' },
                {
                  amount: productAmount.storage_stock_count,
                  type: productAmount.storage_unit,
                }
              )}
            </div>
            {reserves.map((value) => {
              let date = '';

              if (value.date_from && value.date_to) {
                date = `${dayjs(value.date_from).format('DD.MM.YYYY')}-${dayjs(
                  value.date_to
                ).format('DD.MM.YYYY')}`;
              } else if (value.date_from) {
                date = intl.formatMessage(
                  { id: 'product.reserve_date_from' },
                  { date: dayjs(value.date_from).format('DD.MM.YYYY') }
                );
              } else if (value.date_to) {
                date = intl.formatMessage(
                  { id: 'product.reserve_date_to' },
                  { date: dayjs(value.date_to).format('DD.MM.YYYY') }
                );
              }

              return (
                <div key={`${value.amount_from}-${value.amount_to}-${date}`}>
                  {intl.formatMessage(
                    { id: 'product.availability_ranges' },
                    {
                      amount_from: value.amount_from,
                      amount_to: value.amount_to,
                      date: date,
                      type: productAmount.storage_unit,
                    }
                  )}
                </div>
              );
            })}
          </div>
        </StyledPriceAvailabilityCard>
      </CustomGrid>
    );
  };

  const renderIngredients = () => (
    <div className={'product-tab-ingredients'}>
      {!!product?.ingredients && (
        <p className={'product-tab-text'}>{product.ingredients}</p>
      )}
      {hasNutritionalValues && !!product?.nutritional_values && (
        <>
          <p className={'product-tab-text'}>
            {intl.formatMessage({ id: 'nutrition.hundred_gs_contain' })}
          </p>
          <div className={'nutrition-table'}>
            {[
              ...Object.entries(product.nutritional_values),
              ['allergens', product?.allergens],
              ['storage_conditions', product?.storage_conditions],
            ]
              .filter(([, value]) => value && (value > 0 || isNaN(+value)))
              .map(([key, value]) => (
                <div className={'nutrition-row'} key={key}>
                  <span className={'nutrition-name'}>
                    {intl.formatMessage({ id: `nutrition.${key}` })}
                  </span>
                  <span className={'nutrition-value'}>
                    {value}
                    {GRAM_NUTRIENTS.includes(String(key)) ? 'g' : ''}
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );

  const renderProducer = () => (
    <div className={'product-tab-producer'}>
      {farmDescription ? (
        <DescriptionWithShowMore showMore={isMdDown} text={farmDescription} />
      ) : null}
      <Button
        type={'primary'}
        className={'product-tab-farm-btn'}
        label={intl.formatMessage({ id: 'product_view.go_to_producer_page' })}
        onClick={onGoToFarm}
      />
    </div>
  );

  const items = useMemo(
    () =>
      [
        hasPriceAvailability && {
          key: ProductTabKey.PRICE_AVAILABILITY,
          label: intl.formatMessage({ id: 'product.price_availability' }),
          children: renderPriceAvailability(),
        },
        {
          key: ProductTabKey.PRODUCER,
          label: intl.formatMessage({
            id: product?.producer_name
              ? 'product.farm_distributor'
              : 'product.farm_owner',
          }),
          children: renderProducer(),
        },
        hasIngredients && {
          key: ProductTabKey.INGREDIENTS,
          label: intl.formatMessage({ id: 'product.ingredients' }),
          children: renderIngredients(),
        },
        hasDeliveries && {
          key: ProductTabKey.DELIVERY,
          label: intl.formatMessage({ id: 'product.delivery' }),
          children: (
            <div className={'product-tab-delivery'}>
              <RenderProductDelivery product={product} />
            </div>
          ),
        },
        hasReviews && {
          key: ProductTabKey.REVIEWS,
          label: intl.formatMessage({ id: 'product.reviews' }),
          children: <div className={'product-tab-reviews'} />,
        },
      ].filter(Boolean) as {
        key: string;
        label: string;
        children: JSX.Element;
      }[],
    [product, productAmount, isEmarket, isMdDown, farmDescription]
  );

  const [activeKey, setActiveKey] = useState<string>();
  const selectedKey = items.some((item) => item.key === activeKey)
    ? activeKey
    : items[0]?.key;

  return (
    <div className={'product-tabs'}>
      <Tabs
        type={'line'}
        items={items}
        activeKey={selectedKey}
        onChange={setActiveKey}
      />
    </div>
  );
};

export default ProductTabs;
